import { PixelData } from "@/core/utils/image-data"
import {
	getMaskedPixels,
	getMean,
	getMeanV3,
	linear_to_srgb,
	lms_to_xyz,
	normalize_xyz,
	rgb_to_yuv,
	shapeRGBA,
	srgb_to_linear,
	srgb_to_xyz,
	xyz_to_lms,
	xyz_to_srgb,
	yuv_to_rgb,
} from "@/lib/ai/calibration/ai-calibration-utils"
import { whiteBalanceToXY, XYtoWhiteBalance } from "@/core/utils/temperature"
import { XYtoXYZ, XYZtoXY } from "@/core/utils/color-conversion"
import { FaceDetectionResult } from "@/lib/ai/detection/face-detection"
import { clamp } from "@/utils/math"
import { vec3 } from "gl-matrix"
import { logAiInfo } from "@/lib/ai/utils"
import { applyTransform, srgbToLinear } from "../color-match/color-space"
import { getWeightedMeanRgb } from "../color-match/utils"
import { illuminants } from "@/core/utils/color"
import { getAdjustmentRange } from "@/core/adjustments"
import { Adjustments } from "@/core/types"
import { Sentry } from "@/telemetry/sentry"

export interface AutoWBParams {
	temperatureDelta: number
	tintDelta: number
}

export interface EstimatedIlluminant {
	overall: vec3
	image: vec3
	face: vec3 | undefined
	skinWeight: number
}

/**
 * Estimate scene illuminant from image pixels + face data, then compute auto WB deltas.
 *
 * In LMS: D65 / estimated = gain = asShot / adjusted (that's how our shader applies temp/tint)
 *   => adjusted = (asShot × estimated) / D65
 *
 * When computing on top of a preset the chain is: asShot→preset→adjusted.
 *   asShot/preset × D65/estimated = asShot/adjusted
 *   => adjusted = (preset × estimated) / D65
 * So the formula is the same — just use preset WB as `base`.
 *
 * See {@link getAutoWBFromIlluminant} for the full derivation and post-hoc corrections.
 */
export function getAutoWB(options: {
	imageDataSmall: PixelData // should be downsampled (max length = 300)
	imageDataLarge: PixelData // should be downsampled (max length = 2000)
	baseTemperature: number
	baseTint: number
	targetIlluminant: vec3
	faceDetectionResults: FaceDetectionResult[]
}): AutoWBParams {
	const src_white_point = getRobustSkinIlluminant(
		options.imageDataSmall,
		options.imageDataLarge,
		options.faceDetectionResults,
	)
	return getAutoWBFromIlluminant({
		src_white_point,
		targetIlluminant: options.targetIlluminant,
		baseTemperature: options.baseTemperature,
		baseTint: options.baseTint,
	})
}

/**
 * Compute auto white-balance deltas given an estimated scene illuminant (`src_white_point`).
 *
 * ### Derivation (Bradford chromatic adaptation)
 *
 * The camera stores an "as-shot" WB (the `base` temperature/tint). We want to
 * find an `adjusted` WB that neutralises the estimated scene illuminant toward
 * the target (default D65).
 *
 * In LMS (Bradford) space the chromatic adaptation gain is:
 *
 *     gain = LMS(D65) / LMS(estimated)
 *
 * Our shader applies temperature/tint as: gain' = LMS(base) / LMS(adjusted)
 * Equating the two gains:
 *
 *     LMS(adjusted) = LMS(base) * LMS(estimated) / LMS(D65)
 *
 * Convert `adjusted` back to XY chromaticity → temperature/tint → subtract
 * `base` to get deltas.
 *
 * When computing on top of a preset (preset replaces base), the formula is
 * identical: `LMS(adjusted) = LMS(preset) * LMS(estimated) / LMS(D65)`.
 *
 * ### Post-hoc corrections
 *
 * - Tint is scaled down (×0.6) and clamped — tint errors are perceptually
 *   louder than temperature errors.
 * - A warm bias (+7% of absolute temperature, weighted by skin presence) is
 *   added to avoid the common "too blue" auto-WB look.
 * - For faceless sRGB-input images with large negative temp deltas, an extra
 *   warm nudge prevents over-correction toward blue.
 * - Final delta is clamped to ±20% of the base temperature.
 *
 * Reference for chromatic adaptation background:
 * https://santhalakshminarayana.github.io/blog/chromatic-adaptation
 */
export function getAutoWBFromIlluminant(options: {
	src_white_point: EstimatedIlluminant
	targetIlluminant?: vec3
	baseTemperature: number
	baseTint: number
	withoutFace?: boolean
}): AutoWBParams {
	const {
		src_white_point,
		targetIlluminant = illuminants.D65,
		baseTemperature,
		baseTint,
		withoutFace = false,
	} = options
	const xyz_src = withoutFace
		? srgb_to_xyz(src_white_point.image)
		: srgb_to_xyz(src_white_point.overall)

	const lms_estimated = xyz_to_lms(xyz_src)
	const lms_D65 = xyz_to_lms(targetIlluminant)

	const xyz_base = XYtoXYZ(whiteBalanceToXY([baseTemperature, baseTint]))

	const lms_base = xyz_to_lms(xyz_base)

	const lms_adjusted: vec3 = [
		(lms_base[0] * lms_estimated[0]) / lms_D65[0],
		(lms_base[1] * lms_estimated[1]) / lms_D65[1],
		(lms_base[2] * lms_estimated[2]) / lms_D65[2],
	]

	const xyz = lms_to_xyz(lms_adjusted)

	let [temp, tint] = XYtoWhiteBalance(XYZtoXY(xyz))
	temp = temp - baseTemperature
	tint = tint - baseTint
	logAiInfo(`AWB initial d-temp: ${temp}`)

	const autoTintScale = 0.6
	tint = clamp(tint * autoTintScale, -20, 20)

	const skinWeight = withoutFace ? 0 : src_white_point.skinWeight
	// add a constant 7% temperature
	const initialTemp = baseTemperature
	let deltaT = 0.07 * (temp + initialTemp) * (skinWeight + 0.3)
	if (skinWeight == 0 && temp < -150) {
		// srgb input: prevent too much blue
		const scale = 0.25 * Math.min(1, (-temp - 150) / 100)
		deltaT += scale * Math.abs(temp)
	}
	temp = clamp(temp + Math.min(deltaT, 350), -0.2 * initialTemp, 0.2 * initialTemp)

	logAiInfo(`AWB d-tint: ${tint}, d-temp: ${temp}, deltaT: ${deltaT}, skinWeight: ${skinWeight}`)

	if (!Number.isFinite(temp) || !Number.isFinite(tint)) {
		// Diagnostic-only: any non-finite value here means earlier math
		// (illuminant estimation, chromatic adaptation, or XY ↔ temp/tint
		// conversion) produced NaN/Inf. We intentionally do NOT substitute a
		// safe fallback — propagating NaN lets the downstream check in
		// `image-processing.ts` surface a user-visible error rather than
		// silently shipping a broken-but-plausible-looking auto WB.
		Sentry.captureMessage("Auto WB produced non-finite temp/tint", {
			level: "warning",
			extra: {
				temp,
				tint,
				deltaT,
				skinWeight,
				baseTemperature,
				baseTint,
				withoutFace,
				src_white_point,
				targetIlluminant,
				lms_estimated,
				lms_D65,
				lms_base,
			},
		})
	}

	return {
		temperatureDelta: temp,
		tintDelta: tint,
	}
}

/**
 * Auto-WB specifically tuned for the color match pipeline.
 *
 * Unlike standalone auto-WB (which uses the skin-weighted overall illuminant),
 * here we blend the face-aware estimate with the image-only (no-face) estimate
 * via `imageWeight` (default 0.5). The goal is a **conservative, robust** WB
 * base that corrects extreme WB cases (e.g. strong indoor tungsten) without
 * over-fitting to skin tone differences between photos. The color match LUT
 * handles the fine color grading on top of this base.
 *
 * When used to compute bias between input and reference (in `getBaseWB`), the
 * same image-heavy weighting makes the comparison more robust — it measures
 * scene WB difference rather than skin-tone difference, which varies naturally
 * between subjects.
 */
export function getAutoWBParamsForColorMatch(options: {
	illuminant: EstimatedIlluminant
	asShotTemperature: number
	asShotTint: number
	scale?: number
	imageWeight?: number
}): Partial<Adjustments> {
	const { illuminant, asShotTemperature, asShotTint, scale = 1, imageWeight = 0.5 } = options
	const autoWbDeltas = getAutoWBFromIlluminant({
		src_white_point: illuminant,
		baseTemperature: asShotTemperature,
		baseTint: asShotTint,
	})
	const autoWbDeltasWithoutFace = getAutoWBFromIlluminant({
		src_white_point: illuminant,
		baseTemperature: asShotTemperature,
		baseTint: asShotTint,
		withoutFace: true,
	})

	const deltaT =
		autoWbDeltas.temperatureDelta * (1 - imageWeight) +
		autoWbDeltasWithoutFace.temperatureDelta * imageWeight
	const deltaTint =
		autoWbDeltas.tintDelta * (1 - imageWeight) + autoWbDeltasWithoutFace.tintDelta * imageWeight
	logAiInfo("autoWb for color match", {
		autoWbDeltas,
		autoWbDeltasWithoutFace,
		deltaT,
		deltaTint,
	})
	const autoWbParams = {
		temperature: clamp(
			deltaT * scale + asShotTemperature,
			...getAdjustmentRange("temperature"),
		),
		tint: clamp(deltaTint * scale + asShotTint, ...getAdjustmentRange("tint")),
	}
	return autoWbParams
}

/**
 * Robust gray-world illuminant estimation.
 * Selects near-achromatic ("gray") pixels in YUV space using a chromaticity
 * threshold T, then averages their UV to estimate the scene illuminant.
 * Falls back to a looser threshold (0.16) or all pixels if too few grays are found.
 */
function getRobustIlluminant(srgb: vec3[], mask: number[] | null = null, T = 0.1321) {
	if (mask) {
		srgb = srgb.filter((_, i) => mask[i])
	}

	const yuv = srgb.map(rgb_to_yuv)
	const eps = 0.00001

	const F = yuv.map(([y, u, v]) => (Math.abs(u) + Math.abs(v)) / Math.max(eps, y))

	let grays = yuv.filter((_, i) => <number>F[i] < T)

	const gray_pixels_percent = grays.length / srgb.length
	if (gray_pixels_percent < 0.008) {
		grays = yuv.filter((_, i) => <number>F[i] < 0.16)
		logAiInfo("Increase T: gray_pixels_percent, T", grays.length / srgb.length, 0.16)
		if (grays.length / srgb.length < 0.001) grays = yuv
	}

	const u_bar = getMean(grays.map((yuv) => yuv[1]))
	const v_bar = getMean(grays.map((yuv) => yuv[2]))

	return yuv_to_rgb([100 / 255, u_bar, v_bar])
}

/**
 * Combine gray-world illuminant with face skin-tone illuminant.
 * Uses mean face color (mean computed in linear space) blended with the image-wide
 * gray-world estimate with skin weight, blended in XYZ space.
 * Skin weight is ~0.7 but ramps down linearly when faces are tiny (<0.25% of image).
 */
export function getRobustSkinIlluminant(
	imageDataSmall: PixelData | ImageData, // should be downsampled (max length = 300)
	imageDataLarge: PixelData | ImageData, // should be downsampled (max length = 2000)
	faceDetectionResults: FaceDetectionResult[],
	faceData?: {
		facePercent: number
		faceColors: Float32Array | null
		faceWeights: Float32Array | null
	},
): EstimatedIlluminant {
	const T = 0.1321
	const data = imageDataSmall.data
	const allIlluminant = getRobustIlluminant(shapeRGBA(data), null, T)

	if (faceDetectionResults.length > 0) {
		let facePercent: number = 0
		let avg_ch: vec3 | null = null
		if (faceData?.faceColors && faceData?.faceWeights) {
			const { faceColors, faceWeights } = faceData
			facePercent = faceData.facePercent
			const lnFaceColors = applyTransform(faceColors, srgbToLinear)
			avg_ch = getWeightedMeanRgb(lnFaceColors, faceWeights)
		}
		if (!avg_ch) {
			const { maskedPercent, maskedPixels } = getMaskedPixels(
				imageDataLarge,
				faceDetectionResults,
				true,
			)
			facePercent = maskedPercent

			// No usable skin pixels (degenerate face boxes, all-false skin masks, or
			// face boxes outside the image). Skipping face data avoids dividing by
			// zero in getMeanV3 below; behave as if there were no faces.
			if (maskedPixels.length === 0 || facePercent <= 0.0001) {
				return {
					overall: allIlluminant,
					image: allIlluminant,
					face: undefined,
					skinWeight: 0,
				}
			}

			const maskedImg = shapeRGBA(maskedPixels)
			const lnImg = maskedImg.map(srgb_to_linear)
			avg_ch = getMeanV3(lnImg)
		}

		let skin_weight = 0.7
		if (facePercent < 0.0025) {
			skin_weight *= facePercent / 0.0025
		}

		const skinIlluminant = linear_to_srgb(avg_ch)
		const allIlluminantXyz = normalize_xyz(srgb_to_xyz(allIlluminant))
		const skinIlluminantXyz = normalize_xyz(srgb_to_xyz(skinIlluminant))

		// prettier-ignore
		const xyz: vec3 = [
			allIlluminantXyz[0] * (1 - skin_weight) + skinIlluminantXyz[0] * skin_weight,
			allIlluminantXyz[1] * (1 - skin_weight) + skinIlluminantXyz[1] * skin_weight,
			allIlluminantXyz[2] * (1 - skin_weight) + skinIlluminantXyz[2] * skin_weight
		]

		return {
			overall: xyz_to_srgb(xyz),
			image: allIlluminant,
			face: skinIlluminant,
			skinWeight: skin_weight,
		}
	}

	return { overall: allIlluminant, image: allIlluminant, face: undefined, skinWeight: 0 }
}
